/**
 * The paperwork every person here has to hand in, and what happens to it.
 *
 * ## Why this hangs off the user account and not the roster
 *
 * Because everybody has these, not only technicians. A salesperson has a KRA
 * PIN and an accountant has a bank letter exactly as a field engineer does, and
 * a system that could only hold a technician's documents would send half the
 * staff back to a paper folder.
 *
 * It is also the half of a person that always exists. A roster record and a
 * login were two things nothing could join until recently, and three of four
 * technician accounts had no roster record at all. Hanging personnel documents
 * off the roster would have inherited that fault and lost somebody's
 * certificate of good conduct with it.
 *
 * ## Why approval is a state on the document and not a separate table
 *
 * There is exactly one decision per document and it is the last one: approved,
 * or sent back with a reason. A history table would be the right answer if a
 * document could be approved twice, and it cannot — replacing a rejected one
 * uploads a new document rather than editing the old.
 */
/**
 * What a person in Kenya is asked for when they join.
 *
 * Thirteen, and each one is asked for because somebody downstream needs it:
 * payroll cannot file a return without the KRA PIN, the bank cannot be paid
 * into without a letter, and a pole-climbing certificate is the difference
 * between a dispatch and a refusal.
 *
 * The list is here rather than in the database because it changes when the law
 * does, not when a business does — SHIF replacing NHIF in 2024 is the sort of
 * edit that belongs in a commit with a reason attached, not in a settings
 * screen where nobody can see why it moved.
 */
export const DOCUMENT_KINDS = [
    {
        key: 'national_id',
        label: 'National ID',
        hint: 'Both sides, on one page or as two files.',
        required: true,
    },
    {
        key: 'kra_pin',
        label: 'KRA PIN certificate',
        hint: 'Downloaded from iTax. Payroll cannot file a return without it.',
        required: true,
    },
    {
        key: 'nssf',
        label: 'NSSF card or number',
        hint: 'The card, or a statement showing your number.',
        required: true,
    },
    {
        key: 'shif',
        label: 'SHIF registration',
        hint: 'Formerly NHIF. Your registration confirmation.',
        required: true,
    },
    {
        key: 'bank_letter',
        label: 'Bank details',
        hint: 'A bank letter or a statement header showing the account name and number.',
        required: true,
    },
    {
        key: 'passport_photo',
        label: 'Passport photograph',
        hint: 'Recent, plain background. A clear phone photo is fine.',
        required: true,
    },
    {
        key: 'cv',
        label: 'Curriculum vitae',
        hint: 'The one you applied with.',
        required: true,
    },
    {
        key: 'academic',
        label: 'Academic certificates',
        hint: 'Highest qualification. Several pages in one file is fine.',
        required: true,
    },
    {
        key: 'good_conduct',
        label: 'Certificate of good conduct',
        hint: 'From the DCI. If yours is still being processed, upload the receipt.',
        required: true,
    },
    {
        key: 'next_of_kin',
        label: 'Next of kin details',
        hint: 'Name, relationship and a telephone number we can reach in an emergency.',
        required: true,
    },
    {
        key: 'birth_certificate',
        label: 'Birth certificate',
        hint: 'Yours. Needed for some statutory filings.',
        required: false,
    },
    {
        key: 'driving_licence',
        label: 'Driving licence',
        hint: 'Only if you drive a company vehicle.',
        required: false,
    },
    {
        key: 'professional_cert',
        label: 'Professional certificates',
        hint: 'Fibre, pole climbing, first aid — anything your work requires.',
        required: false,
    },
];
export const DOCUMENT_KEYS = DOCUMENT_KINDS.map((kind) => kind.key);
export function documentKind(key) {
    return DOCUMENT_KINDS.find((kind) => kind.key === key) ?? null;
}
/**
 * What may be uploaded.
 *
 * PDF, and the two image types a phone produces. An earlier sketch of this said
 * PDF only, which is right for a certificate and wrong for the two things most
 * people photograph rather than scan — a national ID and a passport photograph.
 * Insisting on PDF for those means somebody hunts for a converter, and a rule
 * that sends people to a random website with a picture of their ID is worse
 * than the rule it enforces.
 */
export const DOCUMENT_TYPES = new Map([
    ['application/pdf', 'pdf'],
    ['image/jpeg', 'jpg'],
    ['image/png', 'png'],
]);
/**
 * Five megabytes.
 *
 * Enough for a scanned multi-page certificate and short of what a modern phone
 * produces at full resolution, which is the point: a 12MB photograph of an ID
 * card is no more readable than a 2MB one and costs somebody their bundle to
 * upload.
 */
export const DOCUMENT_MAX_BYTES = 5 * 1024 * 1024;
/** How long somebody has to hand everything in. */
export const DOCUMENTS_DUE_DAYS = 5;
/**
 * How far through somebody is.
 *
 * Counted over the *required* kinds only. A driving licence nobody needs must
 * never make a file look incomplete, and counting optional documents in the
 * total is how a screen ends up telling a salesperson they are missing a
 * pole-climbing certificate.
 *
 * A rejected document counts as rejected and not as missing, because those are
 * different conversations: one is "you have not sent it", the other is "you
 * sent it and it was not readable".
 */
export function documentProgress(held) {
    const required = DOCUMENT_KINDS.filter((kind) => kind.required);
    /*
     * The latest word per kind. Somebody rejected once and approved on the
     * second attempt is approved, and the caller is not required to have sorted
     * or filtered anything before asking.
     */
    const best = new Map();
    for (const one of held) {
        const current = best.get(one.kind);
        if (current === 'approved')
            continue;
        if (current === 'pending' && one.status === 'rejected')
            continue;
        best.set(one.kind, one.status);
    }
    let approved = 0;
    let pending = 0;
    let rejected = 0;
    let missing = 0;
    for (const kind of required) {
        switch (best.get(kind.key)) {
            case 'approved':
                approved += 1;
                break;
            case 'pending':
                pending += 1;
                break;
            case 'rejected':
                rejected += 1;
                break;
            default:
                missing += 1;
        }
    }
    return {
        approved,
        pending,
        rejected,
        missing,
        required: required.length,
        complete: approved === required.length,
    };
}
/**
 * Whether the person it belongs to may replace it.
 *
 * Not once it is approved. An approved document is a decision somebody made
 * and recorded, and letting the subject quietly swap the file underneath it
 * would make every approval meaningless — the reviewer looked at one thing and
 * the record would show another.
 *
 * Rejected and pending may both be replaced: one because it has to be, the
 * other because there is no reason to make somebody wait for a refusal before
 * correcting an obvious mistake.
 */
export function mayReplace(status) {
    return status !== 'approved';
}
//# sourceMappingURL=employee-documents.js.map